import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    contentChild,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    model,
    ModelSignal,
    OnDestroy,
    output,
    OutputEmitterRef,
    Renderer2,
    signal,
    TemplateRef,
    viewChild,
    viewChildren,
    WritableSignal,
} from '@angular/core';

import { AssertService } from '@connect-services/client/services/assert.service';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

export type SelectFiberOption = {
    labelsWithStyle: { color?: string; font?: string; label: string }[];
    searchLabel: string;
    value: string;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-select-fiber',
    styleUrl: './select-fiber.component.scss',
    templateUrl: './select-fiber.component.html',
})
export class SelectFiberComponent implements OnDestroy {
    public readonly defaultValue = input<SelectFiberOption | undefined>(undefined);
    public readonly label: InputSignal<string | undefined> = input<string>();
    public readonly options = model.required<SelectFiberOption[]>();
    public optionSelected: OutputEmitterRef<string | undefined> = output<string | undefined>();
    public readonly placeholder: InputSignal<string> = input<string>('enterText');
    public readonly selectedOption: ModelSignal<SelectFiberOption | undefined> = model<SelectFiberOption | undefined>(
        undefined,
    );

    protected readonly bottomOption = contentChild(TemplateRef);
    protected readonly CheckboxState = CheckboxState;
    protected readonly defaultOptions: WritableSignal<SelectFiberOption[]> = signal([]);
    protected disabledTooltipList: boolean[] = [];
    protected dropdownWidth: number | null = null;
    protected readonly isOpen: WritableSignal<boolean> = signal(false);
    protected readonly isSelectOptionsOverflowScreen = computed(() => {
        if (!this.selectOptionsNativeElement()) {
            return false;
        }

        return (
            window.innerHeight -
                (this.selectOptionsNativeElement()!.offsetTop + this.selectOptionsNativeElement()!.offsetHeight) <
            0
        );
    });
    protected searchValue: string = '';

    private changeDetector = inject(ChangeDetectorRef);
    private elementRef = inject(ElementRef);
    private globalClickListener: (() => void) | null = null;
    private renderer = inject(Renderer2);
    private readonly selectContainer = viewChild<ElementRef<HTMLDivElement>>('selectContainer');
    private readonly selectInput = viewChild<ElementRef<HTMLInputElement>>('selectInput');
    private readonly selectOptionElements = viewChildren<ElementRef<HTMLOptionElement>>('selectOptionElement');
    private readonly selectOptionNativeElements = computed(() =>
        this.selectOptionElements().map((ref) => ref.nativeElement),
    );
    private readonly selectOptions = viewChild<ElementRef<HTMLInputElement>>('selectOptions');
    private readonly selectOptionsNativeElement = computed(() => this.selectOptions()?.nativeElement);

    public constructor() {
        effect(() => {
            if (!this.searchValue.length) {
                this.defaultOptions.set(this.options());
            }
        });

        effect(() => {
            this.selectedOption.set(this.defaultValue());
        });
    }

    public filterOptions(): void {
        this.options.set(
            this.defaultOptions().filter((option) =>
                option.searchLabel.toLowerCase().includes(this.searchValue.toLowerCase()),
            ),
        );
    }

    public isOptionSelected(option: SelectFiberOption): boolean {
        return this.selectedOption()?.value === option.value;
    }

    public ngOnDestroy(): void {
        this.removeGlobalClickListener();
    }

    public onInputClick(event: MouseEvent): void {
        // avoid closing the select when we just want to focus the input
        if (!this.isOpen()) {
            return;
        }

        event.stopPropagation();
    }

    public resetSelection(event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }

        this.resetSelectedOptions();
    }

    public toggleOption(option: SelectFiberOption): void {
        if (this.selectedOption()?.value === option.value) {
            this.resetSelection();

            return;
        }

        this.selectedOption.set(option);

        this.isOpen.set(false);

        this.optionSelected.emit(option.value);
    }

    public toggleSelect(): void {
        this.isOpen.set(!this.isOpen());

        if (!this.isOpen()) {
            this.removeGlobalClickListener();

            return;
        }

        this.addGlobalClickListener();
        this.calculateDropdownPosition();
        this.changeDetector.detectChanges();
        this.setDisabledTooltipList();
        this.selectInput()!.nativeElement.focus();

        if (!this.isSelectOptionsOverflowScreen()) {
            return;
        }

        this.selectOptionsNativeElement()?.classList.add('bottom-2.5');
    }

    protected resetSelectedOptions(): void {
        this.selectedOption.set(undefined);
        this.optionSelected.emit(undefined);
    }

    private addGlobalClickListener(): void {
        if (this.globalClickListener) {
            return;
        }

        this.globalClickListener = this.renderer.listen('document', 'click', (event: Event) => {
            event.stopPropagation();

            AssertService.hasKey(event.target, 'id');
            AssertService.isString(event.target['id']);

            if (
                !['selected-option', 'multi-selected-options', 'select-div', 'select-input'].includes(
                    event.target['id'],
                ) &&
                !this.elementRef.nativeElement.contains(event.target)
            ) {
                this.isOpen.set(false);
                this.removeGlobalClickListener();
            }
        });
    }

    private calculateDropdownPosition(): void {
        if (!this.selectContainer) {
            return;
        }

        this.dropdownWidth = this.selectContainer()!.nativeElement.offsetWidth;
    }

    private removeGlobalClickListener(): void {
        if (!this.globalClickListener) {
            return;
        }

        this.globalClickListener();
        this.globalClickListener = null;
    }

    private setDisabledTooltipList(): void {
        if (!this.selectOptionNativeElements()) {
            return;
        }

        const selectOptionElements = this.selectOptionNativeElements();

        for (const selectOptionElement of selectOptionElements) {
            const paragraph = selectOptionElement?.querySelector('p');

            if (!paragraph) {
                this.disabledTooltipList.push(true);

                continue;
            }

            this.disabledTooltipList.push(!(paragraph.offsetWidth < paragraph.scrollWidth));
        }
    }
}
